"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { StepCard } from "./StepCard";
import type { StepStatus, SwapQuote } from "./types";

interface Step3Props {
  status: StepStatus;
  isConnected: boolean;
  activeAccountId: string;
  xlmAmount: string;
  setXlmAmount: (v: string) => void;
  swapQuote: SwapQuote;
  onFund: () => void;
  onGoBack?: () => void;
}

export function Step3FundAccount({
  status,
  isConnected,
  activeAccountId,
  xlmAmount,
  setXlmAmount,
  swapQuote,
  onFund,
  onGoBack,
}: Step3Props) {
  const xlm = parseFloat(xlmAmount) || 0;
  const isProcessing = status === "in-progress";

  return (
    <StepCard
      stepNumber={3}
      title="Fund Smart Account"
      description="Send XLM to your smart account for swapping and LP provision"
      status={status}
      onGoBack={onGoBack}
      completedSummary={
        <span>Funded with {String(xlm)} XLM</span>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="text-sm text-muted-foreground whitespace-nowrap">XLM to send:</div>
          <Input
            type="number"
            value={xlmAmount}
            onChange={(e) => { setXlmAmount(e.target.value); }}
            className="w-28 font-mono"
            min="1"
            step="1"
          />
          <div className="text-sm font-medium">XLM</div>
        </div>

        <div className="bg-muted rounded-md p-3 text-sm space-y-1">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Half swapped to USDC</span>
            <span className="font-mono">{String(xlm / 2)} XLM</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Half kept for LP pairing</span>
            <span className="font-mono">{String(xlm / 2)} XLM</span>
          </div>
          <div className="flex justify-between border-t border-border pt-1 font-medium">
            <span>Est. USDC from swap</span>
            <span className="font-mono">
              {swapQuote.loading ? (
                <Loader2 className="w-3 h-3 animate-spin inline" />
              ) : swapQuote.estimatedUsdc ? (
                `~${swapQuote.estimatedUsdc} USDC`
              ) : (
                "..."
              )}
            </span>
          </div>
        </div>

        <Button
          onClick={onFund}
          disabled={!activeAccountId || !isConnected || isProcessing}
          className="w-full"
          variant="secondary"
        >
          {isProcessing ? "Sending XLM..." : `Send ${String(xlm)} XLM to Account`}
        </Button>
      </div>
    </StepCard>
  );
}
